import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import PageContainer from "../pageContainer";
import theme, { when } from "../../styles/theme";
import { SectionTitle } from "../typography";
import { Table, Thead, Tbody, Tr, Td, Th } from "../table";
import { useInView } from "react-intersection-observer";
import { useLocalization } from "gatsby-theme-i18n";
import {
  Button,
  ButtonDownloadIcon,
  ButtonIconContainer,
  ButtonRightIcon,
} from "../button.js";
import Link from '../link.js';

export const fragment = graphql`
  fragment AnnouncementsContent on ContentfulPageSectionAnnouncements {
    title
    announcements {
      title
      date
      announcementDocument {
        publicUrl
      }
    }
    internal {
      type
    }
  }
`;

const translations = {
  en: {
    announcement: "Announcement",
    date: "Date",
    document: "Document",
    download: "Download",
    more: "Show More",
  },
  zh: {
    announcement: "公告",
    date: "發佈日期",
    document: "文件",
    download: "下戴文件",
    more: "展示更多",
  },
};

const AnnouncementsSection = styled(PageContainer)`
  margin-top: 10rem;
`;

const StyledTh = styled(Th)`
  &:first-child {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  &:last-child {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  ${when("lg")} {
    &:first-child {
      border-bottom-left-radius: 1rem;
      border-top-left-radius: 1rem;
    }

    &:last-child {
      border-bottom-right-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }
`;

const Title = styled.span`
  font-weight: 500;
`;

const DateTh = styled(StyledTh)`
  display: none;

  ${when("sm")} {
    display: table-cell;
  }
`;

const DateTd = styled(Td)`
  display: none;

  ${when("sm")} {
    display: table-cell;
  }
`;

const DateXs = styled.div`
  margin-top: 1rem;
  color: ${(props) => props.theme.colors.darkGrey};

  ${when("sm")} {
    display: none;
  }
`;

const TableContainer = styled.div`
  margin-top: 3rem;
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.md}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.md}rem;
  }

  ${when("lg")} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const AnnouncementsTable = styled(Table)``;

const ButtonContainer = styled.div`
  margin-top: 3rem;
  display: flex;
  justify-content: center;
`;

const DownloadButton = styled(Button)`
  margin-left: auto;
  margin-right: auto;
  max-width: fit-content;
  white-space: nowrap;
`;

const Announcements = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const { title, announcements } = data;

  const { locale } = useLocalization();

  const translation = translations[locale];

  return (
    <AnnouncementsSection
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{title}</SectionTitle>
      <TableContainer>
        <AnnouncementsTable>
          <Thead background={theme.colors.darkBlue}>
            <Tr>
              <StyledTh>{translation.announcement}</StyledTh>
              <DateTh>{translation.date}</DateTh>
              <StyledTh>{translation.document}</StyledTh>
            </Tr>
          </Thead>
          <Tbody>
            {announcements &&
              announcements.slice(0, 5).map((announcement, index) => {
                const date = parseISO(announcement.date);
                return (
                  <Tr key={index}>
                    <Td>
                      <Title>{announcement.title}</Title>
                      <DateXs>{format(date, "dd.MM.yyyy")}</DateXs>
                    </Td>
                    <DateTd>{format(date, "dd.MM.yyyy")}</DateTd>
                    <Td>
                      <DownloadButton
                        as={"a"}
                        href={announcement.announcementDocument?.publicUrl}
                      >
                        <ButtonIconContainer>
                          <ButtonDownloadIcon />
                        </ButtonIconContainer>
                        {translation.download}
                      </DownloadButton>
                    </Td>
                  </Tr>
                );
              })}
          </Tbody>
        </AnnouncementsTable>
      </TableContainer>
      <ButtonContainer>
        <Button as={Link} href="/investor-relationship/announcements/1">
          {translation["more"]}
          <ButtonRightIcon />
        </Button>
      </ButtonContainer>
    </AnnouncementsSection>
  );
};
export const announcementsName = "ContentfulPageSectionAnnouncements";

export default Announcements;
